'use client' // Mark this component as a Client Component

import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SiteHeader } from '@/components/site-header'
import { handleRegister } from '@/app/register/actions' // Import server-side action
import { useState } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useRouter } from 'next/navigation'

export default function Register() {
  const [error, setError] = useState<string | null>(null) // Error state to display message
  const [pending, setPending] = useState(false) // Manual pending state
  const router = useRouter()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setPending(true)
    setError(null)

    const formData = new FormData(e.target as HTMLFormElement)
    const { errorMessage, success, needsVerification, userRole } = await handleRegister(formData)

    setPending(false) // Always reset pending state

    if (errorMessage) {
      setError(errorMessage)
    } else if (success) {
      if (needsVerification) {
        router.push('/auth/verify-email')
      } else {
        if(userRole === 'viewer') {
          router.push('/')
        }
        router.push('/dashboard') 
      }
    }
  }

  return (
    <>
      <SiteHeader />
      <div className="flex h-screen w-screen items-center justify-center">
        <Card className="mx-auto max-w-sm">
          <CardHeader>
            <CardTitle className="text-xl">Sign Up</CardTitle>
            <CardDescription>Enter your information to create an account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              {error && (
                <Alert className="mb-4 bg-red-500">
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <div>
                    <Label htmlFor="first-name">Name</Label>
                    <Input id="name" name="name" placeholder="Name" required />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="m@example.com"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" name="password" type="password" />
                </div>
                <Button type="submit" className="w-full" disabled={pending}>
                  {pending ? 'Creating account...' : 'Create an account'}
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full">
                      Configure Oauth provider
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="text-sm">
                    <div>
                      Session management and authentication are already configured for sign up using
                      the Lucia library. You can add additional auth providers at no cost.
                    </div>
                    <div className="mt-4">
                      <Link
                        href="https://lucia-auth.com/tutorials/github-oauth/nextjs-app"
                        target="_blank"
                        className="underline"
                      >
                        Learn more here.
                      </Link>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="mt-4 text-center text-sm">
                Already have an account?{' '}
                <Link href="/login" className="underline">
                  Sign in
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
